import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardIcon from '@mui/icons-material/Dashboard';
import AppLoader from "../components/AppLoader";
import AppBackground from "../components/layout/AppBackground";
import {
  Typography,
  Box,
  CircularProgress,
  Fade,
  Grow,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { api } from "../services/api";
import {
  Star as StarIcon,
  EmojiEvents as EmojiEventsIcon,
  Whatshot as WhatshotIcon,
  Bolt as BoltIcon,
  FlashOn as FlashOnIcon,
  Chat as ChatIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Mic as MicIcon,
  Explore as ExploreIcon,
  TravelExplore as TravelExploreIcon,
  Repeat as RepeatIcon,
  CenterFocusStrong as CenterFocusStrongIcon,
  Storm as StormIcon,
  LabelImportant as LabelImportantIcon,
  ConnectWithoutContact as ConnectWithoutContactIcon,
  Language as LanguageIcon,
  TrackChanges as TrackChangesIcon,
  ShowChart as ShowChartIcon,
  GraphicEq as GraphicEqIcon
} from "@mui/icons-material";

const AchievementsPage = () => {
  const { t, i18n } = useTranslation("achievements");
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const muiIconMap = {
    Star: StarIcon,
    EmojiEvents: EmojiEventsIcon,
    Whatshot: WhatshotIcon,
    Bolt: BoltIcon,
    FlashOn: FlashOnIcon,
    Chat: ChatIcon,
    RecordVoiceOver: RecordVoiceOverIcon,
    Mic: MicIcon,
    Explore: ExploreIcon,
    TravelExplore: TravelExploreIcon,
    Repeat: RepeatIcon,
    CenterFocusStrong: CenterFocusStrongIcon,
    Storm: StormIcon,
    LabelImportant: LabelImportantIcon,
    ConnectWithoutContact: ConnectWithoutContactIcon,
    Language: LanguageIcon,
    TrackChanges: TrackChangesIcon,
    ShowChart: ShowChartIcon,
    GraphicEq: GraphicEqIcon
  };

  const iconColorMap = {
    primary: "#1976d2",
    secondary: "#9c27b0",
    info: "#0288d1",
    success: "#2e7d32",
    warning: "#ed6c02",
    error: "#d32f2f",
    default: "#aaa",
  };

  useEffect(() => {
    api.get("/api/user/achievements").then((res) => {
      setData(res.data);
      setLoading(false);
    });
  }, []);

  if (loading || !data) {
    return <AppLoader message={t("loading")} />;
  }

  return (
    <AppBackground
      sx={{
        color: "#fff",
        scrollBehavior: "smooth",
      }}
    >
      <Fade in timeout={700}>
        <Box
          sx={{
            backgroundColor: "rgba(0,0,0,0.75)",
            borderRadius: 3,
            px: { xs: 2, md: 4 },
            py: { xs: 3, md: 4 },
            maxWidth: "1000px",
            mx: "auto",
          }}
        >
          <Typography variant="h5" mb={4} fontWeight="bold" sx={{textAlign: { xs: "center", sm: "left" }}}>
            {t("achievements_title")}
          </Typography>

          {/* Tus logros */}
          {data.achievements?.length > 0 && (
            <Grow in timeout={800}>
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t("achievements_unlocked")}
                </Typography>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={2}
                  justifyContent={{ xs: "center", md: "flex-start" }}
                >
                  {data.achievements.map((a) => {
                    const MuiIcon = a.icon_mui && muiIconMap[a.icon_mui];
                    return (
                    <Box
                    key={a.id}
                    sx={{
                        width: "140px",
                        backgroundColor: "#1c1c1c",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 1,
                        transition: "transform 0.3s ease, box-shadow 0.3s ease",
                        "&:hover": {
                        transform: "scale(1.05)",
                        boxShadow: "0 4px 20px rgba(168, 85, 247, 0.3)",
                        },
                        boxShadow: {
                        xs: "0 0 12px rgba(168, 85, 247, 0.4)",
                        sm: "none",
                        },
                        animation: {
                        xs: "glow 2.5s ease-in-out infinite",
                        sm: "none",
                        },
                        "@keyframes glow": {
                        "0%, 100%": { boxShadow: "0 0 12px rgba(168, 85, 247, 0.2)" },
                        "50%": { boxShadow: "0 0 20px rgba(168, 85, 247, 0.7)" },
                        },
                    }}
                    >
                        {MuiIcon ? (
                          <MuiIcon sx={{ fontSize: 40 }} color={a.icon_color} />
                        ) : (
                          <Typography variant="h4">{a.icon}</Typography>
                        )}
                        <Box sx={{ px: 1, pt: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {i18n.language?.startsWith("es")
                              ? a.name_es
                              : a.name_en}
                          </Typography>
                          <Typography variant="caption">
                            {i18n.language?.startsWith("es")
                              ? a.description_es
                              : a.description_en}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Grow>
          )}

          {/* Logros por desbloquear */}
          {data.achievements_pending?.length > 0 && (
            <Grow in timeout={900}>
              <Box sx={{ mt: 5 }}>
                <Typography variant="h6" gutterBottom>
                  {t("achievements_locked")}
                </Typography>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={2}
                  justifyContent={{ xs: "center", md: "flex-start" }}
                >
                  {data.achievements_pending.map((a) => {
                    const MuiIcon = a.icon_mui && muiIconMap[a.icon_mui];
                    return (
                      <Box
                        key={a.id}
                        sx={{
                          width: "140px",
                          backgroundColor: "#1c1c1c",
                          borderRadius: 2,
                          textAlign: "center",
                          py: 2,
                          px: 1,
                          opacity: 0.5,
                          boxShadow: "0 0 8px rgba(255, 255, 255, 0.05)",
                          transition: "none",
                        }}
                      >
                        {MuiIcon ? (
                          <MuiIcon
                            sx={{
                              fontSize: 40,
                              color:
                                iconColorMap[a.icon_color] ||
                                iconColorMap.default,
                            }}
                          />
                        ) : (
                          <Typography variant="h4" sx={{ color: "#aaa" }}>
                            {a.icon || "🔒"}
                          </Typography>
                        )}
                        <Box sx={{ px: 1, pt: 1 }}>
                          <Typography variant="subtitle2" fontWeight="bold">
                            {i18n.language?.startsWith("es")
                              ? a.name_es
                              : a.name_en}
                          </Typography>
                          <Typography variant="caption">
                            {i18n.language?.startsWith("es")
                              ? a.description_es
                              : a.description_en}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Grow>
          )}

          {/* BUTTONS */}
          <Grow in timeout={1000}>
            <Box
            sx={{
                textAlign: "center",
                mt: 4,
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: 1, sm: 2 },
            }}
            >
                <Button
                    variant="outlined"
                    onClick={() => navigate("/dashboard")}
                    sx={{ color: "#fff", borderColor: "#fff", gap: 1, mb: { xs: 1, sm: 0 }}}
                >
                    <DashboardIcon sx={{ fontSize: 20 }} /> {t("back_to_dashboard")}
                </Button>
            </Box>
          </Grow>
        </Box>
      </Fade>
    </AppBackground>
  );
};

export default AchievementsPage;
